/**
 * Barber cards — content pass §1.4 (short) / §2.4 (bio).
 *
 * Typographic, not photographic. The photo cards depended on 200x300
 * background-removed cut-outs that carried rembg artifacts — a white blob on
 * Orlando's forearm, ghost text along his bottom edge — and there is no
 * higher-resolution cut-out to replace them with. A card built from type has
 * no such dependency: it cannot be low-resolution, and it holds whatever the
 * roster becomes.
 *
 * Every value resolves from shop.config.json: display_name, role per locale,
 * years_experience through the tenure template, and show_in_crew to keep the
 * owner out. Nothing here is a fact stored twice.
 *
 * gap-px over a navy-tinted grid background is what draws the hairlines
 * between cards — the gaps are the rules.
 */

import { useCopy, useLocale } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';
import { BrassRule } from './BrassRule';

export default function CrewGrid({ variant = 'short' }: { variant?: 'short' | 'bio' }) {
  const copy = useCopy().home.crew;
  const { t, shop, barbers } = useShopConfig();
  const locale = useLocale();

  // Owner first, then config order. Kenny leads the grid as the shop's
  // face; the rest keep the order the config lists them in.
  const shown = (variant === 'short' ? barbers.filter((b) => b.showOnSite !== false) : barbers)
    .slice()
    .sort((a, b) => Number(Boolean(b.is_owner)) - Number(Boolean(a.is_owner)));

  return (
    <section id="barbers" className="bg-paper px-6 py-16 md:px-12 md:py-24">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 flex flex-col gap-6 md:mb-14 md:flex-row md:items-end md:justify-between md:gap-8">
          <div className="flex flex-col">
            <p className="font-oswald text-[12px] uppercase tracking-[0.24em] text-warm-muted">
              {t(copy.kicker)}
            </p>
            <BrassRule width={48} className="mt-3 mb-6" />
            <h2 className="text-balance font-newsreader text-[34px] font-medium leading-[1.1] text-navy md:text-[46px]">
              {t(copy.headline)}
            </h2>
          </div>
          <a
            href={shop.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group self-start whitespace-nowrap font-newsreader text-[20px] text-slate-body transition-colors hover:text-navy md:self-end"
          >
            {t(copy.bookAny)}
          </a>
        </div>

        <div
          className={`grid grid-cols-1 gap-px border border-navy/15 bg-navy/15 sm:grid-cols-2 ${
            shown.length >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'
          }`}
        >
          {shown.map((b) => (
            <a
              key={b.slug}
              href={b.bookingUrl ?? shop.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex min-h-[440px] flex-col justify-end overflow-hidden bg-navy p-8 transition-transform duration-300 hover:scale-[0.99]"
            >
              <BrassRule width={48} className="mb-6" />

              <h3 className="font-newsreader text-[44px] font-medium leading-none tracking-[-0.03em] text-paper md:text-[56px] lg:text-[40px] xl:text-[48px]">
                {b.display_name}
              </h3>

              <p className="mt-3 font-oswald text-[11px] font-medium uppercase tracking-[0.18em] text-paper/65">
                {b.role[locale]} · {t(copy.tenure, { years: b.years_experience })}
              </p>

              {variant === 'bio' && (
                <p className="mt-4 max-w-[34ch] font-newsreader text-[16px] leading-[1.6] text-paper/70">
                  {t(copy.lines[b.slug].bio)}
                </p>
              )}

              {/* Hidden until hover on pointer devices; the whole card is the
                  link, so nothing is lost where hover does not exist. */}
              <span className="mt-6 flex items-center gap-2 font-oswald text-[11px] font-medium uppercase tracking-[0.16em] text-gold opacity-0 transition duration-300 group-hover:opacity-100">
                {t(copy.bookWith, { name: b.display_name })}
              </span>
            </a>
          ))}
        </div>

        {variant === 'bio' && (
          <p className="mt-10 font-newsreader text-[18px] text-slate-body">{t(copy.noPreference)}</p>
        )}
      </div>
    </section>
  );
}
